import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Observable, of, Subscription } from 'rxjs';
import { Project, VersionInfo } from 'src/app/core/objects/project';
import { PdfFromProjectService } from 'src/app/core/services/pdf-from-project.service';
import { ProjectApiService } from 'src/app/core/services/project-api.service';
import { ProjectService } from 'src/app/core/services/project.service';
import { SaveProjectService } from 'src/app/core/services/save-project.service';
import { UserApiService } from 'src/app/core/services/user-api.service';
import { SharedProject } from 'src/app/core/types';
import { ModalOverlayRef } from 'src/app/modal/modal-overlay-ref';
import { MODAL_DATA } from 'src/app/modal/modal.service';
import { catchError } from 'rxjs/operators';

@Component({
    selector: 'gl-download-pdf',
    templateUrl: './download-pdf.component.html',
    styleUrls: ['./download-pdf.component.scss'],
    standalone: false
})
export class DownloadPdfComponent implements OnInit, OnDestroy {
  public form: FormForPDF;
  public project: Project;
  public currentWorkSpaceIndex: number;
  public imageSrc: SafeUrl;
  private subscriptions: Subscription[] = [];
  public isUserAuthorized = false;
  private currentVersion: number;

  constructor(
    private modalRef: ModalOverlayRef,
    private userApiService: UserApiService,
    private saveProjectService: SaveProjectService,
    private projectApiService: ProjectApiService,
    private projectService: ProjectService,
    private pdfFromProjectService: PdfFromProjectService,
    @Optional() @Inject(MODAL_DATA) public data: string,
    private _sanitizer: DomSanitizer,
  ) {
    this.modalRef.backgroundClicked();
    this.subscriptions.push(
      this.projectService.project.subscribe((project: Project) => (this.project = project)),
    );
    this.subscriptions.push(
      this.projectService.currentWorkSpaceIndex.subscribe(
        (current: number) => (this.currentWorkSpaceIndex = current),
      ),
    );
    this.imageSrc = this._sanitizer.bypassSecurityTrustUrl(this.data);
  }

  private setAuthorizationStatus(): void {
    this.userApiService
      .getUser()
      .pipe(
        catchError((error) => {
          this.isUserAuthorized = error.statusText === 'Unauthorized';
          return of(null);
        }),
      )
      .subscribe((user): void => {
        this.isUserAuthorized = user !== null;
      });
  }

  public ngOnInit() {
    this.form = this.saveProjectService.generateForm();
    this.subscriptions.push(
      this.projectApiService
        .getProjectInfo()
        .pipe(
          catchError(() => {
            return of(null);
          }),
        )
        .subscribe((projectInfo) => {
          if (projectInfo && projectInfo.name) {
            this.form.controls['name'].setValue(projectInfo.name);
          }
          if (projectInfo && projectInfo.note) {
            this.form.controls['note'].setValue(projectInfo.note);
          }
        }),
      this.projectApiService.getProjectVersions().subscribe((versions: VersionInfo[]): void => {
        const isVersions: boolean = versions !== null;
        if (isVersions) {
          const currentVersionInfo: VersionInfo = versions.find(
            (version: VersionInfo) => version.isCurrent,
          );
          if (currentVersionInfo) {
            this.currentVersion = currentVersionInfo.version;
          }
        } else {
          this.currentVersion = null;
        }
      }),
    );
    this.setAuthorizationStatus();
    this.form.addControl('saveOption', new UntypedFormControl(true));
    this.form.addControl('downloadPdfForAllLoading', new UntypedFormControl(false));
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((s: Subscription) => s.unsubscribe());
  }

  public onClose() {
    this.modalRef.close();
  }

  public onSubmit(): void {
    if (!this.form.valid) {
      return;
    }
    const projectData: SharedProject & { description: string } = {
      description: this.saveProjectService.generateProjectDescription(),
      project: this.project,
      name: this.form.value.name,
      note: this.form.value.note,
    };
    if (this.form.value.saveOption && this.isUserAuthorized) {
      this.saveProject(projectData).subscribe(() => {
        this.modalRef.close();
        this.downloadPdf(projectData);
      });
    } else {
      this.modalRef.close();
      this.downloadPdf(projectData);
    }
  }

  public downloadPdf(projectData: SharedProject) {
    if (this.form.value.downloadPdfForAllLoading) {
      this.pdfFromProjectService
        .generateSharePDF(
          projectData,
          undefined,
          {
            imageSrc: this.data,
            currentWorkSpace: this.currentWorkSpaceIndex,
          },
          null,
          this.currentVersion,
        )
        .subscribe();
    } else {
      this.pdfFromProjectService
        .generateSharePDF(
          projectData,
          this.currentWorkSpaceIndex,
          {
            imageSrc: this.data,
            currentWorkSpace: this.currentWorkSpaceIndex,
          },
          null,
          this.currentVersion,
        )
        .subscribe();
    }
  }

  public saveProject(projectData): Observable<any> {
    return this.userApiService.saveProject(projectData);
  }
}

interface FormForPDF extends UntypedFormGroup {
  value: {
    name: string;
    note: string;
    saveOption: string;
    downloadPdfForAllLoading: string;
  };
}
